/**
 * useDepartments Hook
 *
 * Fetches cached department list for routing rule dropdowns.
 * Departments are loaded from CRM and cached via edge-api.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useRole } from '@/contexts/RoleContext';
import { useToast } from '@/hooks/use-toast';
import type { DepartmentInfo } from '@/lib/api/types';

// Re-export for consumers
export type { DepartmentInfo };

// Query keys for departments
export const departmentKeys = {
  all: (selectedOrgId: string | null) =>
    ['crm-fields', 'departments', selectedOrgId] as const,
  list: (selectedOrgId: string | null) =>
    [...departmentKeys.all(selectedOrgId), 'list'] as const,
};

/**
 * Hook for fetching cached departments
 * Returns departments for dropdown values in routing rules
 */
export function useDepartments(enabled = true) {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: departmentKeys.list(selectedOrgId),
    queryFn: async (): Promise<DepartmentInfo[]> => {
      const response = await apiClient.crmFields.getDepartments();
      return response.data.departments || [];
    },
    enabled: enabled && !!selectedOrgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for refreshing departments from CRM
 */
export function useRefreshDepartments() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation({
    mutationFn: () => apiClient.crmFields.refreshDepartments(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: departmentKeys.list(selectedOrgId),
      });
      toast({
        title: t('routing.toast.departmentsRefreshed', 'Departments refreshed'),
        description: t(
          'routing.toast.departmentsRefreshedDescription',
          'Department list has been updated from CRM'
        ),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(
          'routing.toast.departmentsRefreshFailed',
          'Failed to refresh departments'
        ),
        description: error.message,
        variant: 'error',
      });
    },
  });
}
